import Dialog from "@mui/material/Dialog";
import { styled as muiStyled } from "@mui/material/styles";
import React, { useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
    endGlobalLoadingAction,
    startGlobalLoadingAction,
} from "../../app/reducers/loading-slice";
import theme from "../../style/theme";
import { ContainedYellowButton } from "../button/ContainedYellowButton";
import { TextWhiteButton } from "../button/TextWhiteButton";
import { UserExcelFileDropZone } from "../dropzone/UserExcelFileDropZone";
import CloseSmallLineIcon from "../icons/CloseSmallLineIcon/CloseSmallLineIcon";
import { profileState } from "../../app/reducers/profile-slice";
import { validateExcelData } from "../../utils/excel/user-excel-validator";
import { useCreateUserBulkMutation, useGetUserPageQuery } from "../../query/user-query";
import { useQueryClient } from "react-query";
import { UserQueryKey } from "../../query/user-query";
import { SearchUserPagePayload } from "../../types/user/user-payload";

interface Props {
    handleClickExcelDialogClose: () => void;
    searchPayload: SearchUserPagePayload;
}

export const UserExcelUploadDialog: React.FC<Props> = ({
    handleClickExcelDialogClose,
    searchPayload
}) => {
    const dispatch = useAppDispatch();
    const { profile } = useAppSelector(profileState);
    const createUserBulkMutation = useCreateUserBulkMutation();
    const queryClient = useQueryClient();
    const [checkedExcelData, setCheckedExcelData] = useState<any[]>([]);
    const [progress, setProgress] = useState<number>(0);

    const setFiles = async (files: File[]) => {
        if (checkedExcelData.length > 0) {
            toast.error("파일은 하나만 등록할 수 있습니다.");
            return;
        }

        const newExcelData = files.map((file) => ({
            name: file.name,
            file
        }));

        setProgressNumber(30, files);
        setCheckedExcelData([...checkedExcelData, ...newExcelData.slice(0, 1)]); // 첫 번째 파일만 사용

        setProgressNumber(90, files);
        setTimeout(() => {
            setProgressNumber(100, files);
        }, 500);
    };

    const setProgressNumber = (number: number, files: File[]) => {
        if (files.length > 0) {
            setProgress(number);
        }
    };

    const setRemoveFiles = (index: number) => {
        setCheckedExcelData(checkedExcelData.filter((_, idx) => idx !== index));
    };

    const handleClickCreateButton = async () => {
        try {
            if (checkedExcelData.length === 0) {
                toast.error("엑셀 파일을 업로드해주세요.");
                return;
            }

            dispatch(startGlobalLoadingAction());

            const validatedData = await validateExcelData(
                checkedExcelData[0].file,
                profile?.company.id || ""
            );

            createUserBulkMutation.mutate(validatedData, {
                onSuccess: () => {
                    toast.success("배송원이 등록되었습니다.");
                    handleClickExcelDialogClose();
                    dispatch(endGlobalLoadingAction());
                    queryClient.invalidateQueries(UserQueryKey.Page);
                },
                onError: (error: any) => {
                    dispatch(endGlobalLoadingAction());
                },
            });
        } catch (error: any) {
            toast.error(error.message);
            dispatch(endGlobalLoadingAction());
        }
    };

    const initData = () => {
        setCheckedExcelData([]);
    };

    return (
        <BootstrapDialog
            onClose={handleClickExcelDialogClose}
            aria-labelledby="customized-dialog-title"
            open
        >
            <ExcelUploadDialogContainer>
                <TitleContainer>
                    <TitleLeftContainer>배송원 엑셀 등록</TitleLeftContainer>
                    <TitleRightContianer onClick={handleClickExcelDialogClose}>
                        <CloseSmallLineIcon
                            width="1.5rem"
                            height="1.5rem"
                            fill={theme.palette.primitives.gray100}
                        />
                    </TitleRightContianer>
                </TitleContainer>
                <BodyContainer>
                    <UserExcelFileDropZone
                        progress={progress}
                        checkedExcelData={checkedExcelData}
                        setFiles={setFiles}
                        setRemoveFiles={setRemoveFiles}
                    />
                </BodyContainer>
                <FooterContainer>
                    <TextWhiteButton
                        width="114px"
                        height="51px"
                        text="초기화"
                        onClick={initData}
                    />
                    <CreateButtonContainer>
                        <ContainedYellowButton
                            width="258px"
                            height="51px"
                            text="등록하기"
                            onClick={handleClickCreateButton}
                        />
                    </CreateButtonContainer>
                </FooterContainer>
            </ExcelUploadDialogContainer>
        </BootstrapDialog>
    );
};

const ExcelUploadDialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 436px;
  border-radius: 0.5rem;
`;

const TitleContainer = styled.div`
  height: 3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${theme.palette.primitives.black};
`;

const TitleLeftContainer = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray50};
  margin-left: 1.5rem;
`;

const TitleRightContianer = styled.div`
  margin-right: 0.438rem;
  cursor: pointer;
`;

const BodyContainer = styled.div`
  overflow-y: auto;
  padding: 20px 24px;
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 24px 20px;
`;

const CreateButtonContainer = styled.div`
  margin-left: 16px;
`;

const BootstrapDialog = muiStyled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
})); 